import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import type { ResumenCertificado as Resumen } from '@shared/certificados';
import { formatearFecha, textoCaducidad } from '@/utils/avisoCertificado';
import { cn } from '@/lib/utils';

interface Props {
  /** `null`: no hay certificado activo. `undefined`: un servidor antiguo que no envía el campo. */
  certificado: Resumen | null | undefined;
  className?: string;
  /** `false` cuando el nombre ya se ve al lado (p. ej. en la tarjeta del certificado). */
  mostrarNombre?: boolean;
  /**
   * `certificadoValido` del servidor. Solo se usa con un servidor antiguo (`certificado` ausente):
   * se dice lo de antes, «Válido» o «No configurado», en vez de no decir nada.
   */
  valido?: boolean;
}

/**
 * Estado del certificado activo en los paneles: nombre, caducidad y nivel.
 * El nivel se dice con palabras; el icono y el color solo lo refuerzan.
 */
export function ResumenCertificado({ certificado, className, mostrarNombre = true, valido }: Props) {
  // Servidor sin el campo: no se afirma nada del certificado; solo lo que ya decía `certificadoValido`.
  if (certificado === undefined) {
    return valido === undefined ? null : <>{valido ? 'Válido' : 'No configurado'}</>;
  }

  if (certificado === null) {
    return (
      <span className={cn('inline-flex items-center gap-1 text-red-700', className)}>
        <XCircle className="h-3 w-3" aria-hidden="true" />
        No configurado
      </span>
    );
  }

  const { nombre, nivel, diasRestantes, validUntil } = certificado;
  const fecha = formatearFecha(validUntil);

  const [Icono, color, texto] =
    nivel === 'ok' ? [CheckCircle2, 'text-green-700', fecha ? `válido hasta el ${fecha}` : textoCaducidad(validUntil, diasRestantes)] :
    nivel === 'caducado' ? [XCircle, 'text-red-700', fecha ? `caducó el ${fecha}` : textoCaducidad(validUntil, diasRestantes)] :
    nivel === 'urgente' ? [XCircle, 'text-red-700', textoCaducidad(validUntil, diasRestantes)] :
    [AlertTriangle, 'text-amber-700', textoCaducidad(validUntil, diasRestantes)];

  return (
    <span className={cn('inline-flex flex-wrap items-center gap-1', color, className)}>
      <Icono className="h-3 w-3" aria-hidden="true" />
      {mostrarNombre && <span>{nombre}: </span>}
      <span>{texto}</span>
    </span>
  );
}
